import React from "react";
import { ChevronRight, Loader2, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface TimelineStepShellProps {
  icon: LucideIcon;
  iconClassName?: string;
  label: React.ReactNode;
  labelClassName?: string;
  meta?: React.ReactNode;
  isInProgress?: boolean;
  isExpanded?: boolean;
  onToggle?: () => void;
  onClick?: () => void;
  bodyClassName?: string;
  children?: React.ReactNode;
}

/**
 * Shared row shell for a timeline step: chevron (when expandable), icon, label,
 * right-side meta, and a collapsible body. Rows are quiet by default — neutral
 * text with a subtle hover — so color stays in the glyphs and label text.
 */
export function TimelineStepShell({
  icon: Icon,
  iconClassName,
  label,
  labelClassName,
  meta,
  isInProgress,
  isExpanded,
  onToggle,
  onClick,
  bodyClassName,
  children,
}: TimelineStepShellProps) {
  const isExpandable = onToggle !== undefined;
  const isInteractive = isExpandable || onClick !== undefined;

  const rowContent = (
    <>
      {isExpandable ? (
        <ChevronRight
          className={cn(
            "h-[18px] w-[18px] shrink-0 text-muted-foreground/60 transition-transform duration-200",
            isExpanded && "rotate-90",
          )}
        />
      ) : (
        <span className="w-[18px] shrink-0" />
      )}
      <Icon
        className={cn(
          "h-[18px] w-[18px] shrink-0 text-muted-foreground",
          iconClassName,
        )}
      />
      <span
        className={cn(
          "min-w-0 flex-1 truncate text-left text-sm text-foreground/90",
          labelClassName,
        )}
      >
        {label}
      </span>
      {isInProgress ? (
        <Loader2 className="h-[18px] w-[18px] shrink-0 animate-spin text-muted-foreground" />
      ) : (
        meta
      )}
    </>
  );

  return (
    <div>
      {isInteractive ? (
        <button
          type="button"
          onClick={isExpandable ? onToggle : onClick}
          className="flex w-full items-center gap-2 rounded-md px-1.5 py-2 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          {rowContent}
        </button>
      ) : (
        <div className="flex w-full items-center gap-2 px-1.5 py-2">
          {rowContent}
        </div>
      )}
      {isExpandable && isExpanded && children !== undefined && (
        <div className={cn("mb-2 ml-[58px] mt-1", bodyClassName)}>
          {children}
        </div>
      )}
    </div>
  );
}
